/**
 * Navigation and Slug Routing Utilities
 * 
 * Supports:
 * - Root domain ('/') or '/stack' for the Stack view
 * - '/tools' for Tools & Calculators view
 * - '/serum' for Serum Concentration Simulator view
 * - '/calendar' for Protocol Regimen Calendar view
 * 
 * Provides history synchronization, direct linking support, and dynamic page titles.
 */

export const TAB_ROUTES = {
  stack: '/stack',
  tools: '/tools',
  serum: '/serum',
  calendar: '/calendar',
};

export const ROUTE_TITLES = {
  stack: 'Supps & Peps | Protocol Stack Tracker',
  tools: 'Supps & Peps | Peptide Tools & Calculators',
  serum: 'Supps & Peps | Serum Concentration Simulator',
  calendar: 'Supps & Peps | Protocol Regimen Calendar',
};

/**
 * Derives the active tab ID from a URL pathname.
 * Handles root ('/'), '/stack', '/tools', '/serum', '/calendar', with case-insensitivity
 * and trailing slash tolerance.
 *
 * @param {string} [pathname] - URL pathname string
 * @returns {'stack' | 'tools' | 'serum' | 'calendar'}
 */
export function getTabFromPath(pathname = window?.location?.pathname) {
  if (!pathname) return 'stack';

  // Normalize: lower-case, strip trailing slashes, fallback to '/'
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';

  if (normalized === '/' || normalized === '/stack') {
    return 'stack';
  }
  if (normalized === '/tools') {
    return 'tools';
  }
  if (normalized === '/serum') {
    return 'serum';
  }
  if (normalized === '/calendar') {
    return 'calendar';
  }

  return 'stack';
}

/**
 * Returns the canonical URL path for a given tab.
 * @param {'stack' | 'tools' | 'serum' | 'calendar'} tab
 * @returns {string}
 */
export function getPathForTab(tab) {
  return TAB_ROUTES[tab] || '/stack';
}

/**
 * Navigates to a tab by pushing or replacing history state and updating document title.
 * @param {'stack' | 'tools' | 'serum' | 'calendar'} tab
 * @param {boolean} [replace=false]
 */
export function navigateToTab(tab, replace = false) {
  if (typeof window === 'undefined') return;

  const targetPath = getPathForTab(tab);
  const currentNormalized = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '') || '/';

  // Avoid pushing duplicate history entries if already at equivalent route
  const isEquivalent =
    currentNormalized === targetPath ||
    (tab === 'stack' && (currentNormalized === '/' || currentNormalized === '/stack'));

  if (!isEquivalent) {
    if (replace) {
      window.history.replaceState({ tab }, '', targetPath);
    } else {
      window.history.pushState({ tab }, '', targetPath);
    }
  }

  if (ROUTE_TITLES[tab]) {
    document.title = ROUTE_TITLES[tab];
  }
}
